/**
 * Starting a shift.
 *
 * The one number asked for is the cash already in the drawer. It is what the
 * end-of-shift count is measured against, so a shift opened with the wrong
 * figure produces a discrepancy that looks like theft. Hence the plain wording
 * and no default beyond zero.
 */
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Field } from '../../components/ui/Field.jsx';

export function OpenShiftPanel({ onOpen, busy, error }) {
  const [openingAmount, setOpeningAmount] = useState('');
  // Guessed from the clock so it is usually already right — opened in the
  // daytime (6 am to 6 pm) it is the day shift, after that the night shift,
  // the one that runs past midnight — but the cashier can change it.
  const [shiftName, setShiftName] = useState(() => {
    const h = new Date().getHours();
    return h >= 6 && h < 18 ? 'day' : 'night';
  });

  function submit(event) {
    event.preventDefault();
    onOpen({ openingAmount: Number(openingAmount) || 0, shiftName });
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-xl border border-rule bg-surface p-6"
      >
        <h1 className="font-semibold text-xl text-ink">Open your shift</h1>
        <p className="mt-1.5 text-sm text-ink-2">
          Count the cash in the drawer now. Everything you sell today is measured against it.
        </p>

        <label className="mt-5 block">
          <span className="mb-1.5 block text-sm font-medium text-ink-2">Which shift</span>
          <select
            value={shiftName}
            onChange={(e) => setShiftName(e.target.value)}
            className="h-12 w-full rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          >
            <option value="day">Day shift</option>
            <option value="night">Night shift</option>
          </select>
        </label>

        <Field
          className="mt-4"
          label="Cash in the drawer"
          type="number"
          inputMode="decimal"
          min="0"
          step="1"
          autoFocus
          value={openingAmount}
          onChange={(e) => setOpeningAmount(e.target.value)}
          placeholder="0"
          hint="Rupees. Enter 0 if the drawer is empty."
        />

        {error && (
          <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
            {error.message}
          </p>
        )}

        <Button type="submit" size="lg" busy={busy} className="mt-6 w-full">
          Start selling
        </Button>
      </form>
    </div>
  );
}
